import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Heading,
  SimpleGrid,
  Card,
  CardBody,
  Text,
  VStack,
  HStack,
  Badge,
  Button,
  Spinner,
  Alert,
  AlertIcon,
  Icon,
  Progress,
  Flex,
  Avatar,
  useColorModeValue,
} from "@chakra-ui/react";
import {
  FiFolder,
  FiClock,
  FiCheckCircle,
  FiActivity,
  FiCalendar,
  FiUser,
  FiEye,
} from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000/api";

const StaffDashboard = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [myProjects, setMyProjects] = useState([]);
  const [error, setError] = useState(null);

  // Theme colors
  const bgPrimary = useColorModeValue("white", "gray.800");
  const bgSecondary = useColorModeValue("gray.50", "gray.700");
  const textPrimary = useColorModeValue("gray.800", "white");
  const textSecondary = useColorModeValue("gray.600", "gray.300");
  const borderColor = useColorModeValue("gray.200", "gray.600");

  useEffect(() => {
    if (user && token) {
      fetchMyProjects();
    }
  }, [user, token]);

  const fetchMyProjects = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API_URL}/project-staff/user/${user.id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { isActive: true },
        }
      );

      if (response.data.success) {
        setMyProjects(response.data.data.assignments);
      }
    } catch (err) {
      console.error("Error fetching projects:", err);
      setError("Failed to load your projects");
    } finally {
      setLoading(false);
    }
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      project_manager: "purple",
      site_engineer: "green",
      supervisor: "blue",
      viewer: "gray",
    };
    return colors[role] || "gray";
  };

  const getRoleLabel = (role) => {
    const labels = {
      project_manager: "Project Manager",
      site_engineer: "Site Engineer",
      supervisor: "Supervisor",
      viewer: "Viewer",
    };
    return labels[role] || role;
  };

  const getStatusColor = (status) => {
    const colors = {
      active: "green",
      planning: "yellow",
      completed: "blue",
      "on-hold": "orange",
      cancelled: "red",
    };
    return colors[status] || "gray";
  };

  if (loading) {
    return (
      <Box minH="100vh" display="flex" alignItems="center" justifyContent="center">
        <VStack spacing={4}>
          <Spinner size="xl" color="blue.500" thickness="4px" />
          <Text color={textSecondary}>Loading your projects...</Text>
        </VStack>
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxW="full" p={6}>
        <Alert status="error" borderRadius="lg">
          <AlertIcon />
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Box minH="100vh" bg={bgSecondary} py={8}>
      <Container maxW="7xl" px={6}>
        <VStack spacing={8} align="stretch">
          {/* Header */}
          <Box bg={bgPrimary} borderRadius="2xl" p={8} boxShadow="lg">
            <VStack align="start" spacing={4}>
              <HStack spacing={4}>
                <Avatar
                  size="xl"
                  name={`${user?.firstName} ${user?.lastName}`}
                  bg="blue.500"
                />
                <VStack align="start" spacing={1}>
                  <Heading size="xl" color={textPrimary}>
                    Welcome, {user?.firstName}!
                  </Heading>
                  <Text color={textSecondary} fontSize="lg">
                    Your Assigned Projects Dashboard
                  </Text>
                  <Badge colorScheme="blue" fontSize="md" px={3} py={1} borderRadius="full">
                    {myProjects.length} Active {myProjects.length === 1 ? "Project" : "Projects"}
                  </Badge>
                </VStack>
              </HStack>
            </VStack>
          </Box>

          {/* No Projects Message */}
          {myProjects.length === 0 ? (
            <Alert
              status="info"
              variant="subtle"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              textAlign="center"
              minH="400px"
              borderRadius="2xl"
            >
              <Icon as={FiFolder} boxSize={16} color="blue.500" mb={4} />
              <Text fontSize="xl" fontWeight="bold" mb={2}>
                No Projects Assigned Yet
              </Text>
              <Text color={textSecondary} maxW="md">
                You haven't been assigned to any projects yet. Contact your administrator
                to get started.
              </Text>
            </Alert>
          ) : (
            /* Project Cards Grid */
            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
              {myProjects.map((assignment) => {
                const project = assignment.project;
                
                return (
                  <Card
                    key={assignment.id}
                    bg={bgPrimary}
                    borderRadius="xl"
                    boxShadow="md"
                    _hover={{
                      boxShadow: "xl",
                      transform: "translateY(-4px)",
                    }}
                    transition="all 0.2s"
                    cursor="pointer"
                    onClick={() => navigate(`/dashboard/company/projects/${project.id}`)}
                  >
                    <CardBody p={6}>
                      <VStack align="stretch" spacing={4}>
                        {/* Project Header */}
                        <HStack justify="space-between" align="start">
                          <VStack align="start" spacing={1} flex={1}>
                            <Heading size="md" color={textPrimary} noOfLines={2}>
                              {project.name}
                            </Heading>
                            <Text fontSize="sm" color={textSecondary} noOfLines={2}>
                              {project.description || "No description"}
                            </Text>
                          </VStack>
                        </HStack>

                        {/* Your Role */}
                        <HStack justify="space-between">
                          <Text fontSize="sm" color={textSecondary}>
                            Your Role:
                          </Text>
                          <Badge
                            colorScheme={getRoleBadgeColor(assignment.projectRole)}
                            fontSize="xs"
                            px={3}
                            py={1}
                            borderRadius="full"
                          >
                            {getRoleLabel(assignment.projectRole)}
                          </Badge>
                        </HStack>

                        {/* Project Status */}
                        <HStack justify="space-between">
                          <Text fontSize="sm" color={textSecondary}>
                            Status:
                          </Text>
                          <Badge
                            colorScheme={getStatusColor(project.status)}
                            fontSize="xs"
                            px={3}
                            py={1}
                            borderRadius="full"
                          >
                            {project.status}
                          </Badge>
                        </HStack>

                        {/* Project Progress */}
                        {project.progress !== undefined && (
                          <VStack align="stretch" spacing={2}>
                            <HStack justify="space-between">
                              <Text fontSize="sm" color={textSecondary}>
                                Progress:
                              </Text>
                              <Text fontSize="sm" fontWeight="bold" color={textPrimary}>
                                {project.progress?.toFixed(1) || 0}%
                              </Text>
                            </HStack>
                            <Progress
                              value={project.progress || 0}
                              colorScheme="blue"
                              size="sm"
                              borderRadius="full"
                            />
                          </VStack>
                        )}

                        {/* Project Details */}
                        <VStack align="stretch" spacing={2} pt={2} borderTopWidth="1px" borderColor={borderColor}>
                          <HStack>
                            <Icon as={FiCalendar} color="gray.500" boxSize={4} />
                            <Text fontSize="xs" color={textSecondary}>
                              Assigned: {new Date(assignment.assignedAt).toLocaleDateString()}
                            </Text>
                          </HStack>

                          {project.startDate && (
                            <HStack>
                              <Icon as={FiClock} color="gray.500" boxSize={4} />
                              <Text fontSize="xs" color={textSecondary}>
                                Started: {new Date(project.startDate).toLocaleDateString()}
                              </Text>
                            </HStack>
                          )}

                          {project.totalTasks !== undefined && (
                            <HStack>
                              <Icon as={FiCheckCircle} color="gray.500" boxSize={4} />
                              <Text fontSize="xs" color={textSecondary}>
                                Tasks: {project.completedTasks || 0}/{project.totalTasks || 0} completed
                              </Text>
                            </HStack>
                          )}
                        </VStack>

                        {/* View Button */}
                        <Button
                          leftIcon={<FiEye />}
                          colorScheme="blue"
                          size="sm"
                          width="100%"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/dashboard/company/projects/${project.id}`);
                          }}
                        >
                          View Project
                        </Button>
                      </VStack>
                    </CardBody>
                  </Card>
                );
              })}
            </SimpleGrid>
          )}
        </VStack>
      </Container>
    </Box>
  );
};

export default StaffDashboard;
